import React, { useState } from "react";
import {
  Button,
  ButtonGroup,
  Stack,
  IconButton,
  Box,
  Paper,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import UndoIcon from "@mui/icons-material/Undo";
import TimelineIcon from "@mui/icons-material/Timeline";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import PlaceIcon from "@mui/icons-material/Place";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";

interface MapControlPanelProps {
  onDrawPolygon: () => void;
  onDrawPoint: () => void;
  onDrawLine: () => void;
  onStopDrawing: () => void;
  onDeleteFeature: () => void;
  onToggleSelection: () => void;
  isDrawing: boolean;
  isSelectionEnabled: boolean;
  basemap: "mapbox" | "mml-maastokartta" | "mml-taustakartta" | "mml-ortokuva";
  onChangeBasemap: (
    v: "mapbox" | "mml-maastokartta" | "mml-taustakartta" | "mml-ortokuva"
  ) => void;
}

const MapControlPanel: React.FC<MapControlPanelProps> = ({
  onDrawLine,
  onDrawPolygon,
  onDeleteFeature,
  onDrawPoint,
  onStopDrawing,
  onToggleSelection,
  isDrawing,
  isSelectionEnabled,
  basemap,
  onChangeBasemap,
}) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  return (
    <Box
      sx={{
        position: "absolute",
        top: 10,
        left: 10,
        zIndex: 1000,
      }}
    >
      {!isPanelOpen ? (
        <IconButton
          onClick={() => {
            setIsPanelOpen(true);
            if (isSelectionEnabled) {
              onToggleSelection();
            }
          }}
          sx={{
            backgroundColor: "white",
            "&:hover": { backgroundColor: "#f0f0f0" },
            boxShadow: 1,
          }}
        >
          <MenuIcon />
        </IconButton>
      ) : (
        <Paper
          elevation={4}
          sx={{
            p: 2,
            borderRadius: 2,
            minWidth: 200,
            maxWidth: 240,
            backgroundColor: "white",
            boxShadow: 3,
          }}
        >
          <Stack spacing={1}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box fontWeight="bold">Työkalut</Box>
              <IconButton
                onClick={() => {
                  setIsPanelOpen(false);
                  if (!isSelectionEnabled) {
                    onToggleSelection();
                  }
                }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {!isDrawing ? (
              <ButtonGroup
                orientation="vertical"
                variant="outlined"
                size="small"
              >
                <Button onClick={onDrawLine} startIcon={<TimelineIcon />}>
                  Mittaa pituus
                </Button>
                <Button onClick={onDrawPolygon} startIcon={<CropSquareIcon />}>
                  Mittaa alue
                </Button>
                <Button onClick={onDrawPoint} startIcon={<PlaceIcon />}>
                  Lisää piste
                </Button>
                <Button onClick={onDeleteFeature} startIcon={<DeleteIcon />}>
                  Poista kuvio
                </Button>
              </ButtonGroup>
            ) : (
              <Button
                startIcon={<UndoIcon />}
                variant="contained"
                color="secondary"
                size="small"
                onClick={onStopDrawing}
              >
                Lopeta piirtäminen
              </Button>
            )}
            <Divider sx={{ my: 1 }} />

            {/* Basemap selector */}
            <FormControl component="fieldset" size="small">
              <FormLabel component="legend" sx={{ fontSize: 12, mb: 0.5 }}>
                Karttataso
              </FormLabel>
              <RadioGroup
                value={basemap}
                onChange={(e) =>
                  onChangeBasemap(
                    e.target.value as
                      | "mapbox"
                      | "mml-maastokartta"
                      | "mml-taustakartta"
                      | "mml-ortokuva"
                  )
                }
              >
                <FormControlLabel
                  value="mapbox"
                  control={<Radio size="small" />}
                  label="Mapbox (oletus)"
                />
                <FormControlLabel
                  value="mml-maastokartta"
                  control={<Radio size="small" />}
                  label="MML Maastokartta"
                />
                <FormControlLabel
                  value="mml-taustakartta"
                  control={<Radio size="small" />}
                  label="MML Taustakartta"
                />
                <FormControlLabel
                  value="mml-ortokuva"
                  control={<Radio size="small" />}
                  label="MML Ortokuva"
                />
              </RadioGroup>
            </FormControl>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default MapControlPanel;
